import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { useGetCRMCustomerProductInfoByProductNameQuery } from "../../../../slices/ExploreProducts.slice";
import CRMCustomerProductCharts from "../../../CustomerView/CRMCustomerProductCharts";
import CRMCustomerProductInfoTable from "../../Common/CRMCustomerProductInfoTable";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";

export const CustomerEngagementsForProductTab = () => {
  const [searchParams] = useSearchParams();
  const productName: string = searchParams.get("productname") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_NAME;
  const { data, error, isError, isFetching, isSuccess } = useGetCRMCustomerProductInfoByProductNameQuery(productName);
  const filters = useContext(ProductViewFiltersContext);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [filteredTableItems, setFilteredTableItems] = useState<any[]>([]);
  const [chartFilter, setChartFilter] = useState({ filterKey: "", value: "" });
  const [filterChartData, setFilterChartData] = useState({
    salesStage: undefined,
    deploymentStatus: undefined,
    customerProductState: undefined
  });

  const customProps = {
    ProductName: productName,
    ProductViewFilters: filters
  };

  useEffect(() => {
    logEvent("Customer Engagements", "Product View Page", "CustomerEngagementsForProductTab", customProps);
  }, []);

  useEffect(() => {
    if (data?.table?.crmData) {
      setOriginalData(data.table.crmData);
      setFilteredTableItems(data.table.crmData);
    }

    if (error) {
      logException(
        "Product View Page - Customer Engagements Tab",
        "CustomerEngagementsForProductTab",
        "useGetCRMCustomerProductInfoByProductNameQuery",
        error,
        customProps
      );
    }
  }, [data, error]);

  // Client-side filtering logic on filters from ProductViewFiltersContext
  useEffect(() => {
    if (!originalData.length) return;
    const parseMultiSelect = (value?: string) => (value ? value.split(";").map((v) => v.trim()) : []);
    const filtered = originalData.filter((item) => {
      const hasPartnerFilter = parseMultiSelect(filters.hasPartner);
      const majorGrowthFilter = parseMultiSelect(filters.majorGrowth);
      const isCATFilter = parseMultiSelect(filters.isCAT);
      const isFastTrackStrategicFilter = parseMultiSelect(filters.isFastTrackStrategic);
      const isTCTFilter = parseMultiSelect(filters.isTCT);
      const isS500Filter = parseMultiSelect(filters.isS500);
      const regionFilter = parseMultiSelect(filters.region);
      const segmentGroupFilter = parseMultiSelect(filters.segmentGroup);
      const subSegmentFilter = parseMultiSelect(filters.subSegment);
      const isCCPFilter = parseMultiSelect(filters.isCCP);
      return (
        (hasPartnerFilter.length === 0 || hasPartnerFilter.includes(item.hasPartner ? "1" : "0")) &&
        (majorGrowthFilter.length === 0 || majorGrowthFilter.includes(item.majorGrowth)) &&
        (isCATFilter.length === 0 || isCATFilter.includes(item.isCAT ? "1" : "0")) &&
        (isFastTrackStrategicFilter.length === 0 ||
          isFastTrackStrategicFilter.includes(item.isFastTrackStrategic ? "1" : "0")) &&
        (isTCTFilter.length === 0 || isTCTFilter.includes(item.isTCT ? "1" : "0")) &&
        (isS500Filter.length === 0 || isS500Filter.includes(item.isS500 ? "1" : "0")) &&
        (regionFilter.length === 0 || regionFilter.includes(item.region)) &&
        (segmentGroupFilter.length === 0 || segmentGroupFilter.includes(item.segmentGroup)) &&
        (isCCPFilter.length === 0 || isCCPFilter.includes(item.isCCP ? "1" : "0")) &&
        (subSegmentFilter.length === 0 || subSegmentFilter.includes(item.subSegment))
      );
    });

    setFilteredTableItems(filtered);
  }, [
    filters.hasPartner,
    filters.majorGrowth,
    filters.isCAT,
    filters.isFastTrackStrategic,
    filters.isTCT,
    filters.isS500,
    filters.region,
    filters.segmentGroup,
    filters.segment,
    filters.subSegment,
    filters.isCCP,
    originalData
  ]);

  // Chart-based filtering
  useEffect(() => {
    if (!originalData.length) return;

    let chartFilters = { ...filterChartData };
    if (chartFilter.filterKey) {
      chartFilters = { ...chartFilters, [chartFilter.filterKey]: chartFilter.value };
    } else {
      chartFilters = { salesStage: undefined, deploymentStatus: undefined, customerProductState: undefined };
    }

    const filtered = originalData.filter((item: any) => {
      return (
        (chartFilters.salesStage === undefined || item.salesStage === chartFilters.salesStage) &&
        (chartFilters.deploymentStatus === undefined || item.deploymentStatus === chartFilters.deploymentStatus) &&
        (chartFilters.customerProductState === undefined ||
          item.customerProductState === chartFilters.customerProductState)
      );
    });

    setFilterChartData(chartFilters);
    setFilteredTableItems(filtered);
  }, [chartFilter, originalData]);

  const showNoData = () => {
    return (
      <div
        style={{
          padding: 200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: 18,
          fontWeight: 600
        }}
      >
        {`Customer product details are not available for ${productName}.`}
      </div>
    );
  };

  if(!isFetching && isError) {
    return <DisplayAPIsException error={error} height={250} />;
  }

  return (
    <>
      <CRMCustomerProductCharts
        loading={isFetching}
        onSelectedChartFilter={setChartFilter}
        options={{
          filteredData: filteredTableItems,
          originalData: originalData,
          chartOptions: [
            {
              id: 1,
              field: "salesStage",
              key: "customer-view-customer-engagements-customer-product-chart-by-salesStage",
              title: "Sales Stage",
              type: "donut",
              kustoLink: data?.table?.salesStageKustoLink
            },
            {
              id: 2,
              field: "deploymentStatus",
              key: "customer-view-customer-engagements-customer-product-chart-by-deploymentStatus",
              title: "Deployment Status",
              type: "donut",
              kustoLink: data?.table?.deploymentStatusKustoLink
            },
            {
              id: 3,
              field: "customerProductState",
              key: "customer-view-customer-engagements-customer-product-chart-by-customerProductState",
              title: "State",
              type: "donut",
              kustoLink: data?.table?.stateKustoLink
            }
          ]
        }}
      />
      {isFetching ? (
        <CustomShimmer columnCount={11} />
      ) : isSuccess && filteredTableItems.length > 0 ? (
        <CRMCustomerProductInfoTable
          tableData={filteredTableItems}
          productName={productName}
          crmDataKustoLink={data?.table?.crmDataKustoLink}
        />
      ) : (
        showNoData()
      )}
    </>
  );
};
